import { groupBy } from "@ameelio/core";
import { SelectInputBase } from "@ameelio/ui";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetCallRatingsReportQuery } from "./GetCallRatingsReport.generated.ts";

type CallRatingsReportType = Omit<
  GetCallRatingsReportQuery["reports"]["callRatings"][number],
  "__typename"
>;

const CallRatingsCharts = ({ data }: { data: CallRatingsReportType[] }) => {
  const { t } = useTranslation();

  const [systemFilter, setSystemFilter] = useState<string>("all");
  const [facilityFilter, setFacilityFilter] = useState<string>("all");

  const systemFilterOptions = useMemo(() => {
    const systems = [
      "all",
      ...Array.from(new Set(data.map((s) => s.systemName))),
    ];
    return systems.map((name) => ({
      name: name === "all" ? t("All systems") : name,
      value: name,
    }));
  }, [data, t]);

  const facilityFilterOptions = useMemo(() => {
    const facilities = [
      "all",
      ...Array.from(
        new Set(
          data
            .filter(
              (s) =>
                s.systemName === systemFilter ||
                !systemFilter ||
                systemFilter === "all"
            )
            .map((s) => s.facilityName)
        )
      ),
    ];
    return facilities.map((name) => ({
      name: name === "all" ? t("All facilities") : name,
      value: name,
    }));
  }, [data, systemFilter, t]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    const systemData =
      !systemFilter || systemFilter === "all"
        ? data
        : data.filter((s) => s.systemName === systemFilter);
    return facilityFilter && facilityFilter !== "all"
      ? systemData.filter((s) => s.facilityName === facilityFilter)
      : systemData;
  }, [data, systemFilter, facilityFilter]);

  const groupedData = groupBy(filteredData, (item) => item.systemName);

  // Create chart data for each system
  const createChartData = (systemData: CallRatingsReportType[]) => {
    const ratingCounts = groupBy(systemData, (item) => item.callRating);
    return [1, 2, 3, 4, 5].map((rating) => ({
      rating: `${rating} Star${rating !== 1 ? "s" : ""}`,
      count: ratingCounts[rating]?.length || 0,
      ratingValue: rating,
    }));
  };

  // Calculate average rating for a system
  const calculateAverageRating = (systemData: CallRatingsReportType[]) => {
    if (systemData.length === 0) return 0;
    const sum = systemData.reduce((acc, item) => acc + item.callRating, 0);
    return (sum / systemData.length).toFixed(1);
  };

  // Get rating color for MUI Chip
  const getRatingColor = (rating: number) => {
    if (rating >= 4) return "success";
    if (rating >= 3) return "warning";
    return "error";
  };

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <SelectInputBase
            size="small"
            disabled={!data || !data.length}
            sx={{ maxWidth: 300 }}
            value={systemFilter}
            items={systemFilterOptions}
            label={t("System")}
            onChange={(e) => {
              setFacilityFilter("all");
              setSystemFilter(e.target.value as string);
            }}
          />
          <SelectInputBase
            size="small"
            disabled={!data || !data.length}
            sx={{ maxWidth: 300 }}
            value={facilityFilter}
            items={facilityFilterOptions}
            label={t("Facility")}
            onChange={(e) => {
              setFacilityFilter(e.target.value as string);
            }}
          />
        </Stack>
        <Box>
          {Object.entries(groupedData).map(([systemName, systemData]) => {
            const chartData = createChartData(systemData || []);
            const avgRating = calculateAverageRating(systemData || []);
            return (
              <Stack spacing={3} key={systemName}>
                <Box>
                  <Typography
                    variant="h2"
                    sx={{
                      mb: 1,
                    }}
                  >
                    {systemName} System -{" "}
                    {facilityFilter === "all"
                      ? "All Facilities"
                      : facilityFilter}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    <Typography>Average Rating: {avgRating}/5</Typography>
                    <Chip
                      label={`${avgRating} Stars`}
                      size="small"
                      color={getRatingColor(parseFloat(avgRating.toString()))}
                    />
                  </Stack>
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      mb: 1,
                    }}
                  >
                    Rating Distribution
                  </Typography>
                  <BarChart
                    dataset={chartData}
                    xAxis={[{ scaleType: "band", dataKey: "rating" }]}
                    series={[
                      {
                        dataKey: "count",
                        label: "Number of Ratings",
                        color: "#2196f3",
                      },
                    ]}
                    height={300}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      mb: 1,
                    }}
                  >
                    Individual Call Ratings
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Facility</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Rating</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Description</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Comment</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(systemData || []).map((item, index) => (
                          <TableRow
                            // eslint-disable-next-line react/no-array-index-key -- report rows have no stable id
                            key={`${item.facilityName}_${item.systemName}_${index}`}
                          >
                            <TableCell>
                              <Chip
                                label={item.facilityName}
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${item.callRating}/5`}
                                size="small"
                                color={getRatingColor(item.callRating)}
                              />
                            </TableCell>
                            <TableCell>{item.description || "-"}</TableCell>
                            <TableCell>{item.comment || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Stack>
            );
          })}
        </Box>
      </Stack>
    </>
  );
};

export default CallRatingsCharts;
